import type { VizFrame, VizState, VizTrace } from './types';

/**
 * イベント列からフレーム列を作る（viz-protocol.md §5.1）。
 * 空の state から始め、show は state[label] を置き換え、note は state を変えずにフレームを足す。
 * フレーム i は step = i + 1 のイベント適用後の状態。
 */
export function deriveFrames(trace: Pick<VizTrace, 'events'>): VizFrame[] {
  const frames: VizFrame[] = [];
  let state: Record<string, VizState> = {};
  for (const e of trace.events) {
    if (e.t === 'show') {
      const s: VizState = { kind: e.kind, value: e.value };
      if (e.head !== undefined) s.head = e.head;
      if (e.marks !== undefined) s.marks = e.marks;
      state = { ...state, [e.label]: s };
      frames.push({ step: e.step, ticks: e.ticks, state, changed: e.label });
    } else {
      frames.push({ step: e.step, ticks: e.ticks, state, changed: null, note: e.text });
    }
  }
  return frames;
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((x, i) => deepEqual(x, b[i]));
  }
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  const rb = b as Record<string, unknown>;
  const ra = a as Record<string, unknown>;
  return ka.every((k) => Object.hasOwn(rb, k) && deepEqual(ra[k], rb[k]));
}

/** viz-step カードのフレーム（card-schema.md §3.8）。 */
export interface ExpectedFrame {
  state: Record<string, VizState>;
  note?: string;
  ticks?: number;
}

/**
 * 実行から得たフレームとカードの `frames` を比べる（viz-protocol.md §5.1 末尾、verify-cards 用）。
 * 件数が同じで各 state が深い等価。カード側に note / ticks があるフレームだけそれも比べる。
 * 一致すれば null、違えば最初に食い違ったフレームの添字。
 */
export function compareFrames(actual: VizFrame[], expected: ExpectedFrame[]): number | null {
  const n = Math.max(actual.length, expected.length);
  for (let i = 0; i < n; i++) {
    const a = actual[i];
    const e = expected[i];
    if (!a || !e) return i;
    if (!deepEqual(a.state, e.state)) return i;
    if (e.note !== undefined && a.note !== e.note) return i;
    if (e.ticks !== undefined && a.ticks !== e.ticks) return i;
  }
  return null;
}
